import { TenantDTO } from "../MyTenant/tenantsModel"
import { ProjectType, ProjectTypes } from "../../common/constants"

// Address types
export type ShopAddress = {
  address1: string
  city: string
  state: string
  zip: string
  countryCode: string
  county?: string
  latitude?: number
  longitude?: number
}

export type BillingAddress = {
  address1: string
  city: string
  state: string
  zip: string
  countryCode: string
}

// Business details form data
export type BusinessDetails = {
  companyName: string
  websiteAddress: string
  timezone: string
  officePhoneNumber: string
  logoBase64?: string
  shopAddress: ShopAddress
  billingAddress: BillingAddress
  billingAddressSameAsShop: boolean
}

// Work hours form data
export type WorkHours = {
  startTime: string
  endTime: string
  lunchBreakMinutes: number
  autoApplyLunchBreaks: boolean
}

// Services form data
export type ServicesConfig = {
  doInstallations: boolean
  doMaintenance: boolean
  doSnowServices: boolean
}

// Division type
export type Division = {
  id: string
  name: string
  jobTypes: ProjectType[]
}

export const ALL_JOB_TYPES = [ProjectTypes.Project, ProjectTypes.Ticket, ProjectTypes.Maintenance]

export const availableJobTypes = (showServiceTickets: boolean): ProjectType[] =>
  showServiceTickets ? ALL_JOB_TYPES : ALL_JOB_TYPES.filter(jobType => jobType !== ProjectTypes.Ticket)

// Terms and conditions
export type TermsAndConditions = {
  content: string
}

// Employee type
export type Employee = {
  id: string
  name: string
  giveLoginAccess: boolean
  email: string
  roles: string[]
}

// Default terms and conditions template
export const DEFAULT_TERMS_AND_CONDITIONS = `<h3>TERMS AND CONDITIONS</h3>

<p><strong>1. PAYMENT TERMS</strong><br/>
Payment is due within 30 days of invoice date unless otherwise specified. We accept cash, check, and major credit cards.</p>

<p><strong>2. WORK AUTHORIZATION</strong><br/>
Work will commence once this proposal is signed and approved by the client. Client signature constitutes acceptance of all terms and conditions.</p>

<p><strong>3. SCOPE CHANGES</strong><br/>
Any changes to the scope of work must be approved in writing and may result in additional charges.</p>

<p><strong>4. WEATHER CONDITIONS</strong><br/>
Work schedules are weather-dependent. We will reschedule work as needed due to inclement weather without penalty.</p>

<p><strong>5. WARRANTY</strong><br/>
We guarantee our workmanship for one year from completion date. Material warranties are as specified by manufacturers.</p>

<p><strong>6. PROPERTY ACCESS</strong><br/>
Client agrees to provide clear access to work areas and ensure utilities are marked before work begins.</p>

<p><strong>7. PERMITS AND REGULATIONS</strong><br/>
Client is responsible for obtaining necessary permits unless otherwise specified in the proposal.</p>

<p><strong>8. CLEANUP</strong><br/>
We will remove all debris and materials from the work site upon completion.</p>`

// State type
export type AccountSetupState = {
  currentStep: number
  isLoading: boolean
  businessDetails: BusinessDetails
  workHours: WorkHours
  services: ServicesConfig
  divisions: Division[]
  termsAndConditions: TermsAndConditions
  employees: Employee[]
}

// Action types
export type AccountSetupAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'INIT_FROM_TENANT'; payload: TenantDTO }
  | { type: 'UPDATE_BUSINESS_DETAILS'; field: keyof BusinessDetails; value: string }
  | { type: 'SET_LOGO'; payload: string | undefined }
  | { type: 'UPDATE_SHOP_ADDRESS'; payload: Partial<ShopAddress> }
  | { type: 'UPDATE_BILLING_ADDRESS'; payload: Partial<BillingAddress> }
  | { type: 'TOGGLE_BILLING_SAME_AS_SHOP'; payload: boolean }
  | { type: 'SYNC_BILLING_TO_SHOP' }
  // Work Hours actions
  | { type: 'UPDATE_WORK_HOURS'; field: keyof WorkHours; value: string | number | boolean }
  // Services actions
  | { type: 'UPDATE_SERVICES'; field: keyof ServicesConfig; value: boolean }
  // Divisions actions
  | { type: 'ADD_DIVISION'; payload: Division }
  | { type: 'REMOVE_DIVISION'; payload: string }
  | { type: 'UPDATE_DIVISION'; id: string; name: string }
  | { type: 'SET_DIVISIONS'; payload: Division[] }
  // Terms and Conditions actions
  | { type: 'UPDATE_TERMS'; payload: string }
  // Employee actions
  | { type: 'ADD_EMPLOYEE' }
  | { type: 'REMOVE_EMPLOYEE'; payload: string }
  | { type: 'UPDATE_EMPLOYEE'; id: string; field: keyof Employee; value: string | boolean | string[] }

// Service-specific division defaults
export const INSTALLATION_DIVISIONS: Division[] = [
  { id: 'hardscape', name: 'Hardscape', jobTypes: [ProjectTypes.Project] },
  { id: 'softscape', name: 'Softscape', jobTypes: [ProjectTypes.Project] },
  { id: 'lighting', name: 'Lighting', jobTypes: [ProjectTypes.Project] },
  { id: 'water-features', name: 'Water features', jobTypes: [ProjectTypes.Project] },
  { id: 'structures', name: 'Structures', jobTypes: [ProjectTypes.Project] },
  { id: 'fencing', name: 'Fencing', jobTypes: [ProjectTypes.Project] },
  { id: 'pools', name: 'Pools', jobTypes: [ProjectTypes.Project] },
]

export const MAINTENANCE_DIVISIONS: Division[] = [
  { id: 'mowing', name: 'Mowing', jobTypes: [ProjectTypes.Maintenance] },
  { id: 'cleanups', name: 'Cleanups', jobTypes: [ProjectTypes.Maintenance] },
  { id: 'irrigation', name: 'Irrigation', jobTypes: [ProjectTypes.Maintenance] },
]

export const SNOW_DIVISIONS: Division[] = [
  { id: 'snow-removal', name: 'Snow Removal', jobTypes: [ProjectTypes.Maintenance] },
  { id: 'ice-control', name: 'Ice Control', jobTypes: [ProjectTypes.Maintenance] },
]

// Initial state
export const getInitialState = (): AccountSetupState => ({
  currentStep: 0,
  isLoading: true,
  businessDetails: {
    companyName: '',
    websiteAddress: '',
    timezone: '',
    officePhoneNumber: '',
    logoBase64: undefined,
    shopAddress: {
      address1: '',
      city: '',
      state: '',
      zip: '',
      countryCode: 'US',
    },
    billingAddress: {
      address1: '',
      city: '',
      state: '',
      zip: '',
      countryCode: 'US',
    },
    billingAddressSameAsShop: true,
  },
  workHours: {
    startTime: '8:00 AM',
    endTime: '5:00 PM',
    lunchBreakMinutes: 30,
    autoApplyLunchBreaks: true,
  },
  services: {
    doInstallations: false,
    doMaintenance: false,
    doSnowServices: false,
  },
  divisions: [],  // Populated by Step 5 based on selected services
  termsAndConditions: {
    content: DEFAULT_TERMS_AND_CONDITIONS,
  },
  employees: [],
})

// Reducer
export const accountSetupReducer = (
  state: AccountSetupState,
  action: AccountSetupAction
): AccountSetupState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload }

    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1 }

    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(0, state.currentStep - 1) }

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }

    case 'INIT_FROM_TENANT':
      return {
        ...state,
        isLoading: false,
        businessDetails: {
          ...state.businessDetails,
          companyName: action.payload.name || '',
          websiteAddress: action.payload.reviewWebsiteAddress || '',
          timezone: action.payload.timeZoneId || '',
          officePhoneNumber: action.payload.phoneNumber || '',
        }
      }

    case 'UPDATE_BUSINESS_DETAILS':
      return {
        ...state,
        businessDetails: { ...state.businessDetails, [action.field]: action.value }
      }

    case 'SET_LOGO':
      return {
        ...state,
        businessDetails: { ...state.businessDetails, logoBase64: action.payload }
      }

    case 'UPDATE_SHOP_ADDRESS': {
      const newShopAddress = { ...state.businessDetails.shopAddress, ...action.payload }
      // If billing same as shop, auto-sync common fields (exclude shop-only fields)
      const { county: _, latitude: __, longitude: ___, ...syncableFields } = action.payload
      const shouldSyncBilling = state.businessDetails.billingAddressSameAsShop && Object.keys(syncableFields).length > 0
      return {
        ...state,
        businessDetails: {
          ...state.businessDetails,
          shopAddress: newShopAddress,
          ...(shouldSyncBilling ? {
            billingAddress: { ...state.businessDetails.billingAddress, ...syncableFields }
          } : {})
        }
      }
    }

    case 'UPDATE_BILLING_ADDRESS':
      return {
        ...state,
        businessDetails: {
          ...state.businessDetails,
          billingAddress: { ...state.businessDetails.billingAddress, ...action.payload }
        }
      }

    case 'TOGGLE_BILLING_SAME_AS_SHOP':
      return {
        ...state,
        businessDetails: {
          ...state.businessDetails,
          billingAddressSameAsShop: action.payload,
          // If turning on, copy shop to billing
          ...(action.payload ? {
            billingAddress: {
              address1: state.businessDetails.shopAddress.address1,
              city: state.businessDetails.shopAddress.city,
              state: state.businessDetails.shopAddress.state,
              zip: state.businessDetails.shopAddress.zip,
              countryCode: state.businessDetails.shopAddress.countryCode
            }
          } : {})
        }
      }

    case 'SYNC_BILLING_TO_SHOP':
      return {
        ...state,
        businessDetails: {
          ...state.businessDetails,
          billingAddress: {
            address1: state.businessDetails.shopAddress.address1,
            city: state.businessDetails.shopAddress.city,
            state: state.businessDetails.shopAddress.state,
            zip: state.businessDetails.shopAddress.zip,
            countryCode: state.businessDetails.shopAddress.countryCode
          }
        }
      }

    // Work Hours actions
    case 'UPDATE_WORK_HOURS':
      return {
        ...state,
        workHours: { ...state.workHours, [action.field]: action.value }
      }

    // Services actions
    case 'UPDATE_SERVICES':
      return {
        ...state,
        services: { ...state.services, [action.field]: action.value }
      }

    // Divisions actions
    case 'ADD_DIVISION':
      return {
        ...state,
        divisions: [...state.divisions, action.payload]
      }

    case 'REMOVE_DIVISION':
      return {
        ...state,
        divisions: state.divisions.filter(d => d.id !== action.payload)
      }

    case 'UPDATE_DIVISION':
      return {
        ...state,
        divisions: state.divisions.map(d =>
          d.id === action.id ? { ...d, name: action.name } : d
        )
      }

    case 'SET_DIVISIONS':
      return {
        ...state,
        divisions: action.payload
      }

    // Terms and Conditions actions
    case 'UPDATE_TERMS':
      return {
        ...state,
        termsAndConditions: { content: action.payload }
      }

    // Employee actions
    case 'ADD_EMPLOYEE':
      return {
        ...state,
        employees: [
          ...state.employees,
          {
            id: crypto.randomUUID(),
            name: '',
            giveLoginAccess: false,
            email: '',
            roles: []
          }
        ]
      }

    case 'REMOVE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.filter(e => e.id !== action.payload)
      }

    case 'UPDATE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.map(e =>
          e.id === action.id ? { ...e, [action.field]: action.value } : e
        )
      }

    default:
      return state
  }
}
